import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { APPLICATION_STATUS } from '@shared/utils/app-static-data';
import { IIndividualAddress } from 'app/models/individuals/individual-address';
import { IIndividualApplicationDeficiency } from 'app/models/individuals/individual-application-deficiency';
import { IIndividualApplicationDocument } from 'app/models/individuals/individual-application-document';
import { IIndividualApplication } from 'app/models/individuals/individual-application';
import { IIndividualCertificate } from 'app/models/individuals/individual-certificate';
import { IIndividualContactInformation } from 'app/models/individuals/individual-contact-information';
import { IIndividualDashboardData, IIndividualDashboardSummary } from 'app/models/individuals/individual-dashboard';
import { IIndividualFilter } from 'app/models/individuals/individual-filter';
import { IIndividualTesting } from 'app/models/individuals/individual-testing';
import { IIndividualTraining } from 'app/models/individuals/individual-training';
import { IIndividualWorkspace } from 'app/models/individuals/individual-workspace';
import { IIndividual } from 'app/models/individuals/individual';
import { IAtpCourseStudent } from 'app/models/atp/atp-course-student';
import { IAtpPerson } from 'app/models/atp/atp-person';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CommonService } from 'app/services/common/common.service';
import { IAccountAffiliation } from 'app/models/common/account-affiliation';

@Injectable({
  providedIn: 'root'
})
export class IndividualService {
  private readonly api = environment.privateApi;
  private static readonly expiringWindowDays = 180;

  constructor(private http: HttpClient, private commonService: CommonService) {}

  getIndividualDashboardData(): Observable<IIndividualDashboardData> {
    return forkJoin({
      individuals: this.getUserIndividuals({
        filterString: '',
        filterStatus: 'Both',
        expirationStartDate: null,
        expirationEndDate: null,
        disciplineTypes: [],
      }),
      applications: this.getIndividualApplications(),
    }).pipe(
      map(({ individuals, applications }) => {
        const openApplications = applications.filter((application) => this.isVisibleDashboardApplication(application.status));

        return {
          summary: this.buildDashboardSummary(individuals, applications, openApplications),
          individuals,
          applications: openApplications,
        };
      }),
    );
  }

  getUserIndividuals(filter: IIndividualFilter): Observable<IIndividual[]> {
    return this.commonService.getAccountAffiliation().pipe(
      map((affiliations) => this.mapAffiliatedIndividuals(affiliations)),
      switchMap((individuals) => this.enrichWithPersonDetails(individuals)),
      map((individuals) => this.applyIndividualFilter(individuals, filter)),
    );
  }

  getIndividualApplications(personId?: number | null): Observable<IIndividualApplication[]> {
    return this.http.get<any[]>(this.api + 'Person/GetPersonApplications/', { withCredentials: true }).pipe(
      map((applications) => (applications || [])
        .map((application) => this.mapApplication(application))
        .filter((application) => !personId || application.person?.id === personId)
        .sort((left, right) => this.toSortDate(right.applicationDate || right.licenseDate) - this.toSortDate(left.applicationDate || left.licenseDate))),
    );
  }

  getIndividualApplicationById(id: number): Observable<IIndividualApplication> {
    return this.http.get<any>(this.api + 'Person/GetPersonApplicationById/' + id, { withCredentials: true }).pipe(
      map((application) => this.mapApplication(application)),
    );
  }

  searchIndividuals(criteria: { firstName: string; lastName: string; dateOfBirth?: string | Date | null; phone?: string | null; email?: string | null }): Observable<IIndividual[]> {
    const firstName = (criteria?.firstName || '').trim();
    const lastName = (criteria?.lastName || '').trim();
    if (!firstName || !lastName) {
      return of([]);
    }

    let params = new HttpParams()
      .set('filterString', firstName + ' ' + lastName)
      .set('lookupFirstName', firstName)
      .set('lookupLastName', lastName);
    if (criteria?.dateOfBirth) {
      params = params.set('lookupDateOfBirth', criteria.dateOfBirth instanceof Date ? criteria.dateOfBirth.toISOString() : criteria.dateOfBirth);
    }

    if ((criteria?.phone || '').trim()) {
      params = params.set('lookupPhone', (criteria.phone || '').trim());
    }

    if ((criteria?.email || '').trim()) {
      params = params.set('lookupEmail', (criteria.email || '').trim());
    }

    return this.http.get<any[]>(this.api + 'Person/GetPeopleFiltered', { params, withCredentials: true }).pipe(
      map((individuals) => (individuals || []).map((individual) => this.mapIndividual(individual))),
    );
  }

  saveIndividualApplication(application: IIndividualApplication): Observable<IIndividualApplication> {
    return this.http.post<any>(
      this.api + 'Person/SavePersonApplication',
      this.toApplicationDto(application),
      { withCredentials: true },
    ).pipe(
      map((savedApplication) => this.mapApplication(savedApplication)),
    );
  }

  getIndividualWorkspace(personId: number): Observable<IIndividualWorkspace> {
    return forkJoin({
      individual: this.getIndividualById(personId),
      contactInformations: this.getIndividualContactInformations(personId).pipe(catchError(() => of([]))),
      addresses: this.getIndividualAddresses(personId).pipe(catchError(() => of([]))),
      certificates: this.getIndividualCertificates(personId).pipe(catchError(() => of([]))),
      testings: this.getIndividualTestings(personId).pipe(catchError(() => of([]))),
      trainings: this.getIndividualTrainings(personId).pipe(catchError(() => of([]))),
      applications: this.getIndividualApplications(personId).pipe(catchError(() => of([]))),
    }).pipe(
      map((result) => ({
        individual: result.individual,
        contactInformations: result.contactInformations,
        addresses: result.addresses,
        certificates: result.certificates,
        testings: result.testings,
        trainings: result.trainings,
        applications: result.applications,
        affiliations: result.individual.affiliations || [],
      })),
    );
  }

  getIndividualById(id: number): Observable<IIndividual> {
    return this.http.get<any>(this.api + 'Person/GetPersonById/' + id, { withCredentials: true }).pipe(
      map((individual) => this.mapIndividual(individual)),
    );
  }

  saveIndividual(individual: Pick<IIndividual, 'id' | 'firstName' | 'middleName' | 'lastName' | 'title' | 'inactive' | 'response'>): Observable<IIndividual> {
    return this.http.post<any>(
      this.api + 'Person/SavePublicPersonEdit',
      this.toPublicPersonEditDto(individual),
      { withCredentials: true },
    ).pipe(
      map((savedIndividual) => this.mapIndividual(savedIndividual)),
    );
  }

  getIndividualContactInformation(personId: number): Observable<IIndividualContactInformation | null> {
    return this.http.get<any>(this.api + 'Person/GetPersonContactInformationByPersonId/' + personId, { withCredentials: true }).pipe(
      map((contactInformation) => contactInformation ? this.mapContactInformation(contactInformation) : null),
    );
  }

  getIndividualContactInformations(personId: number): Observable<IIndividualContactInformation[]> {
    return this.http.get<any[]>(this.api + 'Person/GetPersonContactInformationsByPersonId/' + personId, { withCredentials: true }).pipe(
      map((contactInformations) => (contactInformations || []).map((contactInformation) => this.mapContactInformation(contactInformation))),
    );
  }

  saveIndividualContactInformation(contactInformation: IIndividualContactInformation): Observable<IIndividualContactInformation> {
    return this.http.post<any>(
      this.api + 'Person/SavePersonContactInformation',
      {
        id: contactInformation.id,
        person: { id: contactInformation.individualId },
        contactInformation: contactInformation.contactInformation,
      },
      { withCredentials: true },
    ).pipe(
      map((savedContactInformation) => this.mapContactInformation(savedContactInformation)),
    );
  }

  deleteIndividualContactInformation(id: number): Observable<any> {
    return this.http.get(this.api + 'Person/DeletePersonContactInformation/' + id, { withCredentials: true });
  }

  getIndividualAddresses(personId: number): Observable<IIndividualAddress[]> {
    return this.http.get<any[]>(this.api + 'Person/GetPersonAddressesByPersonId/' + personId, { withCredentials: true }).pipe(
      map((addresses) => (addresses || []).map((address) => this.mapAddress(address, personId))),
    );
  }

  saveIndividualAddress(address: IIndividualAddress): Observable<IIndividualAddress> {
    return this.http.post<any>(
      this.api + 'Person/SavePersonAddress',
      {
        id: address.id,
        person: { id: address.individualId },
        address: address.address,
        isCurrentPhysical: address.isCurrentPhysical === true,
        isCurrentMailing: address.isCurrentMailing === true,
      },
      { withCredentials: true },
    ).pipe(
      map((savedAddress) => this.mapAddress(savedAddress, address.individualId)),
    );
  }

  deleteIndividualAddress(id: number): Observable<any> {
    return this.http.get(this.api + 'Person/DeletePersonAddress/' + id, { withCredentials: true });
  }

  getIndividualCertificates(personId: number): Observable<IIndividualCertificate[]> {
    return this.http.get<any[]>(this.api + 'Person/GetPersonCertificates/' + personId, { withCredentials: true }).pipe(
      map((certificates) => (certificates || []).map((certificate) => this.mapCertificate(certificate))),
    );
  }

  getIndividualTestings(personId: number): Observable<IIndividualTesting[]> {
    return this.http.get<any[]>(this.api + 'Person/GetPersonTestings/' + personId, { withCredentials: true }).pipe(
      map((testings) => (testings || []).map((testing) => this.mapTesting(testing))),
    );
  }

  getIndividualTrainings(personId: number): Observable<IIndividualTraining[]> {
    return this.http.get<any[]>(this.api + 'Person/GetPersonTrainings/' + personId, { withCredentials: true }).pipe(
      map((trainings) => (trainings || []).map((training) => this.mapTraining(training))),
    );
  }

  getIndividualTrainingHistory(personId: number): Observable<IAtpCourseStudent[]> {
    return this.http.get<IAtpCourseStudent[]>(this.api + 'Atp/GetATPCourseStudentsByStudentId/' + personId, { withCredentials: true });
  }

  getIndividualAtpAffiliations(personId: number): Observable<IAtpPerson[]> {
    return this.http.get<any[]>(this.api + 'Atp/GetATPAffiliationsByPersonId/' + personId, { withCredentials: true }).pipe(
      map((affiliations) => (affiliations || []).map((affiliation) => ({
        ...affiliation,
        requestStatus: affiliation?.requestStatus || null,
        isRequestOnly: affiliation?.isRequestOnly === true,
      }))),
    );
  }

  getAllAffiliationAtps(): Observable<Array<{ id: number; name: string; privateCompany: boolean }>> {
    return this.http.get<any[]>(this.api + 'Atp/GetAllAtpNames', { withCredentials: true }).pipe(
      map((atps) => (atps || []).map((atp) => ({
        id: atp.id,
        name: atp.name,
        privateCompany: atp.privateCompany === true,
      }))),
    );
  }

  getAllAffiliationFirms(): Observable<Array<{ id: number; name: string; isPrivate: boolean }>> {
    return this.http.get<any[]>(this.api + 'Firm/GetAllFirmNames', { withCredentials: true }).pipe(
      map((firms) => (firms || []).map((firm) => ({
        id: firm.id,
        name: firm.name,
        isPrivate: firm.isPrivate === true,
      }))),
    );
  }

  getIndividualUserAccounts(personId: number): Observable<IAccountAffiliation[]> {
    return this.http.get<IAccountAffiliation[]>(this.api + 'Common/GetPersonAffiliations/' + personId, { withCredentials: true });
  }

  saveUserAccountAffiliation(accountAffiliation: IAccountAffiliation): Observable<IAccountAffiliation> {
    return this.commonService.saveAccountAffiliation(accountAffiliation);
  }

  deleteIndividualAtpAffiliation(id: number): Observable<any> {
    return this.http.get(this.api + 'Atp/DeleteATPPerson/' + id, { withCredentials: true });
  }

  getIndividualFirmAffiliations(personId: number): Observable<any[]> {
    return this.http.get<any[]>(this.api + 'Firm/GetFirmPeopleByPersonId/' + personId, { withCredentials: true }).pipe(
      map((affiliations) => (affiliations || []).map((affiliation) => ({
        ...affiliation,
        requestStatus: affiliation?.requestStatus || null,
        isRequestOnly: affiliation?.isRequestOnly === true,
      }))),
    );
  }

  saveIndividualAtpAffiliation(personId: number, atpId: number): Observable<any> {
    return this.requestIndividualAffiliation(personId, 'ATP', atpId);
  }

  saveIndividualFirmAffiliation(personId: number, firmId: number): Observable<any> {
    return this.requestIndividualAffiliation(personId, 'Firm', firmId);
  }

  requestIndividualAffiliation(personId: number, entityType: 'ATP' | 'Firm', entityId: number): Observable<any> {
    return this.http.post(
      this.api + 'Person/RequestIndividualAffiliation',
      {
        personId,
        entityType,
        entityId,
      },
      { withCredentials: true },
    );
  }

  cancelIndividualAffiliationRequest(personId: number, entityType: 'ATP' | 'Firm', entityId: number): Observable<any> {
    return this.http.post(
      this.api + 'Person/CancelIndividualAffiliationRequest',
      {
        personId,
        entityType,
        entityId,
      },
      { withCredentials: true },
    );
  }

  deleteIndividualFirmAffiliation(id: number): Observable<any> {
    return this.http.get(this.api + 'Firm/DeleteFirmPerson/' + id, { withCredentials: true });
  }

  getIndividualApplicationDeficiencies(applicationId: number): Observable<IIndividualApplicationDeficiency[]> {
    return this.http.get<any[]>(this.api + 'Person/GetPersonApplicationDeficiencies/' + applicationId, { withCredentials: true }).pipe(
      map((deficiencies) => (deficiencies || []).map((deficiency) => this.mapDeficiency(deficiency))),
    );
  }

  saveIndividualApplicationDeficiency(deficiency: IIndividualApplicationDeficiency): Observable<IIndividualApplicationDeficiency> {
    return this.http.post<any>(
      this.api + 'Person/SavePersonApplicationDeficiency',
      deficiency,
      { withCredentials: true },
    ).pipe(
      map((savedDeficiency) => this.mapDeficiency(savedDeficiency)),
    );
  }

  getIndividualApplicationDocuments(applicationId: number): Observable<IIndividualApplicationDocument[]> {
    return this.http.get<any[]>(this.api + 'Person/GetPersonApplicationDocuments/' + applicationId, { withCredentials: true }).pipe(
      map((documents) => (documents || []).map((document) => this.mapDocument(document))),
    );
  }

  saveIndividualApplicationDocument(document: IIndividualApplicationDocument): Observable<IIndividualApplicationDocument> {
    return this.http.post<any>(
      this.api + 'Person/SavePersonApplicationDocument',
      {
        id: document.id,
        applicationId: document.applicationId,
        application: document.applicationId > 0 ? { id: document.applicationId } : null,
        document: document.document,
        fileName: document.fileName,
        fileType: document.fileType,
        fileDescription: document.fileDescription,
      },
      { withCredentials: true },
    ).pipe(
      map((savedDocument) => this.mapDocument(savedDocument)),
    );
  }

  deleteIndividualApplicationDocument(id: number): Observable<any> {
    return this.http.get(this.api + 'Person/DeletePersonApplicationDocument/' + id, { withCredentials: true });
  }

  shouldDisplayApplicationDeficiencies(application: IIndividualApplication): boolean {
    const status = this.normalizeStatus(application.status);
    return status === this.normalizeStatus(APPLICATION_STATUS.actionNeeded)
      && this.getUnresolvedApplicationDeficiencyDescriptions(application).length > 0;
  }

  getUnresolvedApplicationDeficiencyDescriptions(application: IIndividualApplication | null | undefined): string[] {
    return (application?.deficiencies || [])
      .filter((deficiency) => !deficiency.isResolved)
      .map((deficiency) => (deficiency.description || '').trim())
      .filter((description) => description.length > 0);
  }

  getUnresolvedApplicationDeficiencyText(application: IIndividualApplication): string {
    return this.getUnresolvedApplicationDeficiencyDescriptions(application).join('\n\n');
  }

  getCertificateSummary(individual: IIndividual): string {
    const summary = this.buildCertificateSummary(individual.certificates || []);
    return summary.active + ' active / ' + summary.expiring + ' expiring / ' + summary.expired + ' expired';
  }

  private buildDashboardSummary(individuals: IIndividual[], applications: IIndividualApplication[], openApplications: IIndividualApplication[]): IIndividualDashboardSummary {
    const certificates = individuals.reduce((allCertificates, individual) => allCertificates.concat(individual.certificates || []), [] as IIndividualCertificate[]);
    const certificateSummary = this.buildCertificateSummary(certificates);

    return {
      affiliatedIndividualCount: individuals.length,
      activeIndividualCount: individuals.filter((individual) => !individual.inactive).length,
      applicationCount: applications.length,
      expiringCertificateCount: certificateSummary.expiring,
      expiredCertificateCount: certificateSummary.expired,
      openApplicationCount: openApplications.length,
      submittedApplicationCount: openApplications.filter((application) => {
        const status = this.normalizeStatus(application.status);
        return status === this.normalizeStatus(APPLICATION_STATUS.submitted)
          || status === this.normalizeStatus(APPLICATION_STATUS.inReview);
      }).length,
      actionNeededApplicationCount: openApplications.filter((application) => this.normalizeStatus(application.status) === this.normalizeStatus(APPLICATION_STATUS.actionNeeded)).length,
    };
  }

  private buildCertificateSummary(certificates: IIndividualCertificate[]): { active: number; expiring: number; expired: number } {
    const today = this.toDateOnly(new Date());
    const expiringThreshold = new Date(today);
    expiringThreshold.setDate(expiringThreshold.getDate() + IndividualService.expiringWindowDays);

    return (certificates || []).reduce((summary, certificate) => {
      if (!certificate.expirationDate) {
        return summary;
      }

      const expirationDate = this.toDateOnly(certificate.expirationDate);
      if (expirationDate < today) {
        summary.expired++;
      } else if (expirationDate <= expiringThreshold) {
        summary.expiring++;
        summary.active++;
      } else {
        summary.active++;
      }

      return summary;
    }, { active: 0, expiring: 0, expired: 0 });
  }

  private mapAffiliatedIndividuals(affiliations: IAccountAffiliation[]): IIndividual[] {
    const individualsById = new Map<number, IIndividual>();

    (affiliations || []).forEach((affiliation) => {
      if (!affiliation?.person || affiliation.status === 'Cancelled') {
        return;
      }

      if (!individualsById.has(affiliation.person.id)) {
        individualsById.set(affiliation.person.id, this.mapIndividual(affiliation.person));
      }
    });

    return Array.from(individualsById.values()).sort((left, right) => {
      const lastNameCompare = (left.lastName || '').localeCompare(right.lastName || '');
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }

      return (left.firstName || '').localeCompare(right.firstName || '');
    });
  }

  private enrichWithPersonDetails(individuals: IIndividual[]): Observable<IIndividual[]> {
    if (!individuals.length) {
      return of([]);
    }

    return forkJoin(
      individuals.map((individual) => forkJoin({
        person: this.getIndividualById(individual.id).pipe(catchError(() => of(individual))),
        certificates: this.getIndividualCertificates(individual.id).pipe(catchError(() => of([]))),
      }).pipe(
        map((result) => {
          const enrichedIndividual = {
            ...result.person,
            certificates: result.certificates,
            affiliations: result.person.affiliations || [],
          } as IIndividual;

          enrichedIndividual.inactive = !result.certificates.some((certificate) => {
            if (!certificate.expirationDate) {
              return false;
            }

            return this.toDateOnly(certificate.expirationDate) >= this.toDateOnly(new Date());
          });

          return enrichedIndividual;
        })
      ))
    );
  }

  private mapIndividualsFromApplications(applications: IIndividualApplication[]): IIndividual[] {
    const individualsById = new Map<number, IIndividual>();

    applications.forEach((application) => {
      const person = application.person;
      if (!person?.id) {
        return;
      }

      const existing = individualsById.get(person.id);
      if (!existing) {
        individualsById.set(person.id, {
          ...person,
          certificates: [...(person.certificates || [])],
          affiliations: [...(person.affiliations || [])],
        });
        return;
      }

      existing.certificates = this.mergeCertificates(existing.certificates, person.certificates || []);
      existing.affiliations = this.mergeStrings(existing.affiliations, person.affiliations || []);
      existing.response = existing.response || person.response;
      existing.inactive = existing.inactive && person.inactive;
    });

    return Array.from(individualsById.values()).sort((left, right) => {
      const lastNameCompare = (left.lastName || '').localeCompare(right.lastName || '');
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }

      return (left.firstName || '').localeCompare(right.firstName || '');
    });
  }

  private mergeCertificates(existingCertificates: IIndividualCertificate[], newCertificates: IIndividualCertificate[]): IIndividualCertificate[] {
    const certificatesById = new Map<number, IIndividualCertificate>();

    existingCertificates.concat(newCertificates).forEach((certificate) => {
      certificatesById.set(certificate.id, certificate);
    });

    return Array.from(certificatesById.values());
  }

  private mergeStrings(existingValues: string[], newValues: string[]): string[] {
    return Array.from(new Set((existingValues || []).concat(newValues || [])));
  }

  private applyIndividualFilter(individuals: IIndividual[], filter: IIndividualFilter): IIndividual[] {
    let filteredIndividuals = [...individuals];
    const filterString = (filter.filterString || '').trim().toLowerCase();

    if (filterString) {
      filteredIndividuals = filteredIndividuals.filter((individual) => [
        individual.id.toString(),
        individual.firstName,
        individual.middleName,
        individual.lastName,
        individual.title,
        (individual.affiliations || []).join(' '),
        (individual.certificates || []).map((certificate) => certificate.codeType).join(' '),
      ].join(' ').toLowerCase().includes(filterString));
    }

    const status = (filter.filterStatus || 'Both').toLowerCase();
    if (status !== 'both' && status !== '') {
      filteredIndividuals = filteredIndividuals.filter((individual) => status === 'inactive' ? individual.inactive : !individual.inactive);
    }

    if ((filter.disciplineTypes || []).length > 0) {
      filteredIndividuals = filteredIndividuals.filter((individual) =>
        (individual.certificates || []).some((certificate) => filter.disciplineTypes.includes(certificate.codeType))
      );
    }

    const expirationStartDate = filter.expirationStartDate ? this.toDateOnly(filter.expirationStartDate) : null;
    const expirationEndDate = filter.expirationEndDate ? this.toDateOnly(filter.expirationEndDate) : null;
    if (expirationStartDate || expirationEndDate) {
      filteredIndividuals = filteredIndividuals.filter((individual) =>
        (individual.certificates || []).some((certificate) => {
          if (!certificate.expirationDate) {
            return false;
          }

          const expirationDate = this.toDateOnly(certificate.expirationDate);
          return (!expirationStartDate || expirationDate >= expirationStartDate)
            && (!expirationEndDate || expirationDate <= expirationEndDate);
        })
      );
    }

    return filteredIndividuals;
  }

  private mapIndividual(value: any): IIndividual {
    return {
      id: value?.id ?? 0,
      firstName: value?.firstName ?? '',
      middleName: value?.middleName ?? '',
      lastName: value?.lastName ?? '',
      title: value?.title ?? '',
      ssn: value?.ssn ?? '',
      dateOfBirth: value?.dateOfBirth ?? null,
      inactive: value?.inactive === true,
      response: value?.response ?? '',
      certificates: (value?.certificates || []).map((certificate: any) => this.mapCertificate(certificate)),
      affiliations: value?.affiliations || [],
    };
  }

  private mapApplication(value: any): IIndividualApplication {
    return {
      id: value?.id ?? 0,
      userID: value?.userID ?? value?.userId ?? null,
      person: value?.person ? this.mapIndividual(value.person) : null,
      status: value?.status ?? APPLICATION_STATUS.inProgress,
      applicationDate: value?.applicationDate ?? value?.licenseDate ?? null,
      licenseDate: value?.licenseDate ?? value?.applicationDate ?? null,
      approverEmail: value?.approverEmail ?? null,
      approvalDate: value?.approvalDate ?? null,
      applicationViableDate: value?.applicationViableDate ?? null,
      lastUpdatedBy: value?.lastUpdatedBy ?? null,
      lastUpdated: value?.lastUpdated ?? null,
      isPublic: value?.isPublic === true,
      renewal: value?.renewal === true,
      previousApplication: value?.previousApplication ?? null,
      ssn: value?.ssn ?? '',
      dateOfBirth: value?.dateOfBirth ?? null,
      title: value?.title ?? '',
      firstName: value?.firstName ?? value?.person?.firstName ?? '',
      middleName: value?.middleName ?? value?.person?.middleName ?? '',
      lastName: value?.lastName ?? value?.person?.lastName ?? '',
      phone: value?.phone ?? '',
      alternatePhone: value?.alternatePhone ?? '',
      faxPhone: value?.faxPhone ?? '',
      email: value?.email ?? '',
      confirmEmail: value?.confirmEmail ?? value?.email ?? '',
      website: value?.website ?? '',
      mailingStreet: value?.mailingStreet ?? '',
      mailingState: value?.mailingState ?? '',
      mailingCity: value?.mailingCity ?? '',
      mailingZip: value?.mailingZip ?? '',
      mailingCounty: value?.mailingCounty ?? '',
      mailingIsPhysical: value?.mailingIsPhysical === true,
      certificateType: value?.certificateType ?? null,
      attestation: value?.attestation === true,
      ssnExempt: value?.ssnExempt === true,
      ssnExemptStatement: value?.ssnExemptStatement ?? '',
      esignature: value?.esignature ?? '',
      repLabel: value?.repLabel ?? '',
      repPhone: value?.repPhone ?? '',
      repAlternatePhone: value?.repAlternatePhone ?? '',
      repFaxPhone: value?.repFaxPhone ?? '',
      repEmail: value?.repEmail ?? '',
      repConfirmEmail: value?.repConfirmEmail ?? '',
      repWebsite: value?.repWebsite ?? '',
      deficiencies: (value?.deficiencies || []).map((deficiency: any) => this.mapDeficiency(deficiency)),
    };
  }

  private toPersonDto(individual: IIndividual): any {
    return {
      id: individual.id,
      firstName: individual.firstName,
      middleName: individual.middleName,
      lastName: individual.lastName,
      title: individual.title,
      ssn: individual.ssn,
      dateOfBirth: individual.dateOfBirth,
      inactive: individual.inactive,
      response: individual.response,
      certificates: individual.certificates || [],
      affiliations: individual.affiliations || [],
    };
  }

  private toPublicPersonEditDto(individual: Pick<IIndividual, 'id' | 'firstName' | 'middleName' | 'lastName' | 'title' | 'inactive' | 'response'>): any {
    return {
      id: individual.id,
      firstName: individual.firstName,
      middleName: individual.middleName,
      lastName: individual.lastName,
      title: individual.title,
      inactive: individual.inactive,
      response: individual.response,
    };
  }

  private toApplicationDto(application: IIndividualApplication): any {
    return {
      id: application.id,
      userID: application.userID,
      person: application.person ? this.toPersonDto(application.person) : null,
      status: application.status,
      applicationDate: application.applicationDate,
      licenseDate: application.licenseDate,
      approverEmail: application.approverEmail,
      approvalDate: application.approvalDate,
      applicationViableDate: application.applicationViableDate,
      lastUpdatedBy: application.lastUpdatedBy,
      lastUpdated: application.lastUpdated || undefined,
      isPublic: application.isPublic,
      renewal: application.renewal,
      previousApplication: application.previousApplication,
      ssn: application.ssn,
      dateOfBirth: application.dateOfBirth,
      title: application.title,
      firstName: application.firstName,
      middleName: application.middleName,
      lastName: application.lastName,
      phone: application.phone,
      alternatePhone: application.alternatePhone,
      faxPhone: application.faxPhone,
      email: application.email,
      confirmEmail: application.confirmEmail,
      website: application.website,
      mailingStreet: application.mailingStreet,
      mailingState: application.mailingState,
      mailingCity: application.mailingCity,
      mailingZip: application.mailingZip,
      mailingCounty: application.mailingCounty,
      mailingIsPhysical: application.mailingIsPhysical,
      certificateType: application.certificateType,
      attestation: application.attestation,
      ssnExempt: application.ssnExempt,
      ssnExemptStatement: application.ssnExemptStatement,
      esignature: application.esignature,
      repLabel: application.repLabel,
      repPhone: application.repPhone,
      repAlternatePhone: application.repAlternatePhone,
      repFaxPhone: application.repFaxPhone,
      repEmail: application.repEmail,
      repConfirmEmail: application.repConfirmEmail,
      repWebsite: application.repWebsite,
    };
  }

  private mapContactInformation(value: any): IIndividualContactInformation {
    return {
      id: value?.id ?? 0,
      person: value?.person ? this.mapIndividual(value.person) : null,
      individualId: value?.person?.id ?? 0,
      contactInformation: value?.contactInformation ?? { id: 0, phone: '', ext: '', cell: '', fax: '', email: '', website: '', contactType: '', label: '' },
    };
  }

  private mapAddress(value: any, personId: number): IIndividualAddress {
    return {
      id: value?.id ?? 0,
      person: value?.person ? this.mapIndividual(value.person) : null,
      individualId: value?.person?.id ?? personId,
      address: value?.address ?? { id: 0, street: '', city: '', state: '', zip: '', county: '', contactType: '' },
      isCurrentPhysical: value?.isCurrentPhysical === true,
      isCurrentMailing: value?.isCurrentMailing === true,
    };
  }

  private mapCertificate(value: any): IIndividualCertificate {
    return {
      id: value?.id ?? 0,
      individual: null,
      stage: value?.stage ?? '',
      codeType: value?.codeType ?? '',
      certificateNumber: value?.certificateNumber ?? '',
      controlNumber: value?.controlNumber ?? '',
      issueDate: value?.issueDate ?? null,
      expirationDate: value?.expirationDate ?? null,
      recertificationDate: value?.recertificationDate ?? null,
      dateOfLetter: value?.dateOfLetter ?? null,
      application: value?.application ? this.mapApplication(value.application) : null,
    };
  }

  private mapTesting(value: any): IIndividualTesting {
    return {
      id: value?.id ?? 0,
      person: value?.person ? this.mapIndividual(value.person) : null,
      individual: value?.person ? this.mapIndividual(value.person) : null,
      personLicenseDocument: null,
      personLicense: value?.personApplication ? this.mapApplication(value.personApplication) : null,
      application: value?.personApplication ? this.mapApplication(value.personApplication) : null,
      codeType: value?.codeType ?? '',
      stage: value?.stage ?? '',
      testLocation: value?.testLocation ?? '',
      testVersion: value?.testVersion ?? '',
      testDate: value?.testDate ?? null,
      testSerial: value?.testSerial ?? '',
      testScore: value?.testScore ?? '',
      passFail: value?.passFail ?? '',
    };
  }

  private mapTraining(value: any): IIndividualTraining {
    return {
      id: value?.id ?? 0,
      person: value?.person ? this.mapIndividual(value.person) : null,
      individual: value?.person ? this.mapIndividual(value.person) : null,
      personLicenseDocument: null,
      personLicense: value?.personApplication ? this.mapApplication(value.personApplication) : null,
      application: value?.personApplication ? this.mapApplication(value.personApplication) : null,
      codeType: value?.codeType ?? '',
      stage: value?.stage ?? '',
      comment: value?.comment ?? '',
      trainingDate: value?.trainingDate ?? null,
      expirationDate: value?.expirationDate ?? null,
    };
  }

  private mapDeficiency(value: any): IIndividualApplicationDeficiency {
    return {
      id: value?.id ?? 0,
      applicationId: value?.applicationId ?? 0,
      description: value?.description ?? '',
      isResolved: value?.isResolved === true,
    };
  }

  private mapDocument(value: any): IIndividualApplicationDocument {
    return {
      id: value?.id ?? 0,
      applicationId: value?.applicationId ?? value?.application?.id ?? 0,
      document: value?.document ?? null,
      fileName: value?.fileName ?? '',
      fileType: value?.fileType ?? '',
      fileDescription: value?.fileDescription ?? '',
    };
  }

  private isVisibleDashboardApplication(status: string): boolean {
    const normalizedStatus = this.normalizeStatus(status);
    return normalizedStatus !== this.normalizeStatus(APPLICATION_STATUS.approved)
      && normalizedStatus !== this.normalizeStatus(APPLICATION_STATUS.cancelled);
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status || '').toString().trim().toLowerCase();
  }

  private toSortDate(value: string | null | undefined): number {
    return value ? new Date(value).getTime() : 0;
  }

  private toDateOnly(value: string | Date): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }
}